<script setup>
import { computed, reactive } from 'vue'
import Icon from "./Icon.vue"
import Button from "./Button.vue"
import Tabs from "./Tabs.vue"
import Tab from "./Tab.vue"

import { mediaUploader, ImageList, selectedImage } from '@utils/mediaUploader'
import VuePictureCropper, { cropper } from 'vue-picture-cropper'

const emit = defineEmits(['update:modelValue', 'select', 'change', 'open', 'close'])

const props = defineProps({
    // modelValue: {
    //     type: String
    // },
    height: {
        type: String,
        default: '200px'
    },
    width: {
        type: String,
        default: '700px'
    },
    title: {
        type: String,
        default: 'Upload Image'
    },
    button: {
        type: [String, Boolean],
        default: 'Select Image'
    },
    accept: {
        type: String,
        default: 'image/png, image/jpg, image/webp'
    }
})

const selectButton = computed(() => {
    if (!selectedImage.value[mediaUploader.state.activeUploader].length || selectedImage.value[mediaUploader.state.activeUploader] == '') return 'Please select an image'
    else return props.button
})

const isSelected = (url) => {
    if (typeof selectedImage.value[mediaUploader.state.activeUploader] == 'undefined') {
        if (mediaUploader.state.multiple) {
            selectedImage.value[mediaUploader.state.activeUploader] = []
        } else {
            selectedImage.value[mediaUploader.state.activeUploader] = ''
        }
    }

    return mediaUploader.state.multiple ? selectedImage.value[mediaUploader.state.activeUploader].includes(url) : selectedImage.value[mediaUploader.state.activeUploader] == url
}

/**
* Crop
*/
const state = reactive({
    cropping: false,
    uploading: false,
    showCrop: false,
    cropBox: true,
    image: null,
})

const select = (url = null, shouldClose = false) => {
    mediaUploader.selectImage(url, shouldClose)
}


/**
 * Clear the crop box
 */
const clearCrop = () => {
    if (!cropper) return
    cropper.clear()
    state.cropBox = false
}

/**
 * Reset the default cropping area
 */
const resetCrop = () => {
    if (!cropper) return
    cropper.reset()
}

const crop = async () => {
    if (!cropper) return
    state.cropping = true
    const blob = await cropper.getBlob()
    state.cropping = false
    if (!blob) return

    const file = new File([blob], 'image.png', { type: 'image/png' })

    state.uploading = true

    const uploaded = await mediaUploader.upload(file)

    console.log('AFTER UPLOAD', uploaded)
    // if (uploaded.success) {
    //     mediaUploader.selectImage(uploaded, true)
    // }
    state.uploading = false
    state.showCrop = false
    state.image = null
}


const cancelCrop = () => {
    state.showCrop = false
    state.image = null
}

const selectFile = (e) => {
    if (mediaUploader.state.crop) {
        // set image as croppping image
        state.showCrop = true
        state.image = URL.createObjectURL(e.target.files[0])
    } else {
        mediaUploader.selectFile(e)
    }
}
</script>
<template>
<div class="group">
    <!-- Modal -->
    <div v-if="mediaUploader.state.open == true" @drop.prevent="mediaUploader.onDrop"
        @dragenter.prevent="mediaUploader.dragover" @dragover.prevent="mediaUploader.dragover"
        @dragleave.prevent="mediaUploader.dragleave" class="relative z-[999999]">
        <div class="z-50 flex justify-center items-center fixed w-full h-full left-0 top-0 bg-white ">
            <div class="absolute left-0 top-0 w-full h-full bg-[rgba(0,0,0,0.5)] z-0 " @click="mediaUploader.close()">
            </div>
            <div v-if="state.showCrop && state.image"
                class="relative rounded w-full bg-white shadow-lg max-w-xl mx-auto z-50 p-4 space-y-4 max-h-screen overflow-y-scroll">
                <div class="flex items-center justify-between">
                    <h3 class="font-semibold text-lg">Edit Image</h3>
                    <Icon name="xClose" size="20px" class="text-[#475467] cursor-pointer" @click="cancelCrop" />
                </div>
                <div :class="{
                    'pointer-events-none opacity-50': state.cropping || state.uploading
                }">
                    <VuePictureCropper :boxStyle="{
                        width: '100%',
                        height: '100%',
                        backgroundColor: '',
                        margin: 'auto',
                    }" :img="state.image" :options="{
                        viewMode: 1,
                        dragMode: 'crop',
                        aspectRatio: mediaUploader.state.ratio,
                    }" @ready="ready" />
                </div>
                <div class="flex items-center justify-between gap-3">
                    <Button type="secondary_color" @click.prevent="resetCrop">Reset</Button>
                    <div class="flex gap-3 items-center">
                        <Button type="secondary_gray" @click.prevent="clearCrop">Skip Cropping</Button>
                        <Button @click.prevent="crop" :disable="state.cropping || state.uploading">
                            {{ state.cropping ? 'Saving...' : state.uploading ? 'Uploading...' : 'Save' }}</Button>
                    </div>
                </div>
            </div>
            <div v-else @click.stop
                class="media-uploader-animate z-50 bg-[white] rounded-lg p-4 flex flex-col gap-4 w-[80vw] h-[80vh]">
                <!-- header -->
                <div class="flex items-center justify-between w-full h-8">
                    <div class="text-[#101828] text-lg font-semibold">{{ props.title || 'Upload Image' }}</div>
                    <Icon name="xClose" size="20px" class="text-[#475467] cursor-pointer"
                        @click="mediaUploader.close()" />
                </div>
                <!-- modal tab -->
                <div
                    class="border bg-[#F2F4F7] flex gap-2 items-center rounded-lg border-solid border-[#F2F4F7] p-1 relative">
                    <div v-for="(name, tab) in mediaUploader.state.tabs" :key="tab"
                        @click="mediaUploader.changeTab(tab)"
                        class="w-full px-3 py-2 text-xs font-semibold text-center transition-all rounded-md cursor-pointer md:text-sm whitespace-nowrap duration-400"
                        :class="mediaUploader.state.tab == tab ? 'text-[#344054] bg-[#fff] shadow-[0px_1px_2px_0px_#1018280F,0px_1px_3px_0px_#1018281A]' : 'text-[#667085] hover:text-[#344054]'">
                        {{ name }}</div>
                </div>
                <!-- modal content -->
                <div class="w-full h-full overflow-hidden">
                    <!-- Library -->
                    <div v-if="mediaUploader.state.tab === 'library'" class="relative h-full">
                        <div class="pb-0 overflow-auto"
                            :class="[0 !== ImageList.length ? 'h-[calc(100%-42px)]' : 'h-full']">
                            <div v-if="0 == ImageList.length"
                                class="text-[#101828] text-xl font-semibold leading-8 mb-1.5 flex items-center justify-center h-[80%]">
                                You haven’t uploaded any images
                            </div>
                            <div v-else class="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-4 xl:grid-cols-6 gap-4 p-4">
                                <div v-for="(image, index) in ImageList" :key="index"
                                    class="relative aspect-square cursor-pointer flex items-center justify-center"
                                    @click="select(image.url, false)" @dblclick="select(image.url, true)">
                                    <Icon v-if="isSelected(image.url)" name="check" size="20px"
                                        class="text-[#fff] absolute top-2 right-2 bg-[#7F56D9] rounded-full p-1 z-10" />
                                    <img :src="image.url" class="w-full rounded-lg" :alt="'Image ' + (index + 1)">
                                    <div class="absolute inset-0 border border-[#EAECF0] rounded-lg"></div>
                                </div>
                            </div>
                        </div>
                        <div v-if="0 !== ImageList.length" class="w-full">
                            <!-- Note: image can be selected without clicking save button -->
                            <Button size="lg" type="primary" radius="square"
                                :disable="mediaUploader.state.multiple ? selectedImage[mediaUploader.state.activeUploader].length == 0 : selectedImage[mediaUploader.state.activeUploader] == ''"
                                classButton="w-full" @click="mediaUploader.close">{{ selectButton }}</Button>
                        </div>
                    </div>
                    <!-- Uploader  -->
                    <div v-else class="h-full text-center">
                        <div class="flex items-center justify-center w-full h-full">
                            <label v-if="!mediaUploader.state.uploading" for="dropzone-file"
                                class="flex flex-col items-center justify-center w-full h-full rounded cursor-pointer bg-gray-50 hover:bg-gray-100 ">
                                <div class="flex flex-col items-center justify-center pt-5 pb-6">
                                    <Icon name="uploadCloud02" size="20px" class="text-[#475467]" />
                                    <p class="mb-2 text-sm text-gray-500 dark:text-gray-400">
                                        <span class="font-semibold">Click to upload</span> or drag and drop
                                    </p>
                                    <!-- <div class="text-[#475467] text-xs font-normal"> JPG, PNG, WEBP ({{ props.width }}x{{ props.height }}) </div> -->
                                    <div class="text-[#475467] text-xs font-normal"> JPG, PNG, WEBP</div>
                                </div>
                                <input id="dropzone-file" type="file" class="hidden"
                                    :multiple="mediaUploader.state.multiple" :accept="props.accept"
                                    @change="selectFile($event)" />
                            </label>
                            <div v-if="mediaUploader.state.uploading"
                                class="flex items-center justify-center w-full h-full text-sm rounded bg-gray-50">
                                Uploading. Please wait <Icon name="loading01" size="14px" class="animate-spin">
                                </Icon>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- new tab -->
                <!-- <Tabs default="library" :class="isDisabled">
                    <Tab id="library">Library</Tab>
                    <Tab id="upload">Upload</Tab>
                
                    <template #library>
                        library
                        </template>
                    <template #upload>
                        upload
                        </template>

                </Tabs> -->
            </div>
        </div>
    </div>
</div>
</template>
<style lang="scss"
    scoped>
    // .media-uploader-animate {
    //     animation: modal 0.4s ease-in-out;
    // }

    @keyframes modal {
        0% {
            transform: scale(0.95);
            opacity: 0;
        }

        45% {
            transform: scale(1.02);
            opacity: 1;
        }

        80% {
            transform: scale(0.98);
        }

        100% {
            transform: scale(1);
        }
    }
</style>